import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { accountMappingImportService } from "~/services/contabilidad/accountMappingImportService";
import type {
  AccountMappingCSVRow,
  AccountMappingImportResult,
} from "~/types/contabilidad";

interface ImportMappingsParams {
  rows: AccountMappingCSVRow[];
  templateName: string;
  companyId: string;
}

export function useImportAccountMappings() {
  const queryClient = useQueryClient();

  return useMutation<
    AccountMappingImportResult,
    Error,
    ImportMappingsParams
  >({
    mutationFn: async (params: ImportMappingsParams) => {
      return await accountMappingImportService.importMappings(
        params.rows,
        params.templateName,
        params.companyId
      );
    },
    onSuccess: (result) => {
      if (result.success) {
        // Invalidate account mappings cache
        queryClient.invalidateQueries({ queryKey: ["account-mappings"] });
        queryClient.invalidateQueries({ queryKey: ["mapping-templates"] });

        toast.success(
          `Successfully imported ${result.imported} account mappings${result.skipped > 0 ? ` (${result.skipped} skipped)` : ""}`
        );
      } else {
        toast.error(
          `Import failed: ${result.errors[0]?.error || "Unknown error"}`
        );
      }
    },
    onError: (error: Error) => {
      toast.error(`Import failed: ${error.message}`);
    },
  });
}

export function useParseCSV() {
  return useMutation<AccountMappingCSVRow[], Error, File>({
    mutationFn: async (file: File) => {
      return await accountMappingImportService.parseCSV(file);
    },
    onError: (error: Error) => {
      toast.error(`Failed to parse CSV: ${error.message}`);
    },
  });
}

export function useValidateMappings() {
  return useMutation<
    Awaited<ReturnType<typeof accountMappingImportService.validateMappings>>,
    Error,
    { rows: AccountMappingCSVRow[]; companyId: string }
  >({
    mutationFn: async ({ rows, companyId }) => {
      return await accountMappingImportService.validateMappings(
        rows,
        companyId
      );
    },
    onError: (error: Error) => {
      toast.error(`Validation failed: ${error.message}`);
    },
  });
}
